import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeVersionHistory.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, Eye, RotateCcw, Tag } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositePagination from '../components/MicrositePagination/MicrositePagination'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeRollbackConfirmModal from '../components/MicrositeRollbackConfirmModal/MicrositeRollbackConfirmModal'

const PAGE_LIMIT = 20

const TAB_FILTER_OPTIONS = [
    { value: '', label: 'All tabs' },
    { value: 'general', label: 'General' },
    { value: 'theme', label: 'Theme' },
    { value: 'nav_top', label: 'Navigation (top)' },
    { value: 'nav_bottom', label: 'Navigation (bottom)' },
    { value: 'social', label: 'Social' },
    { value: 'integrations', label: 'Integrations' },
    { value: 'seoConfig', label: 'SEO settings' }
]

/**
 * MicrositeVersionHistory
 * Global, cross-tab view of version history across the whole
 * microsite — complements MicrositeVersionDrawer (which is scoped
 * to a single tab and opened contextually from each screen). This
 * page is for salon owners who want to see "what changed recently"
 * across everything in one place, rather than tab by tab.
 */
const MicrositeVersionHistory = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])
    const [tabFilter, setTabFilter] = useState('')
    const [cursorStack, setCursorStack] = useState([0])
    const [nextCursor, setNextCursor] = useState(null)

    const [rollbackTarget, setRollbackTarget] = useState(null)
    const [rollingBack, setRollingBack] = useState(false)

    const currentCursor = cursorStack[cursorStack.length - 1]

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadVersions = useCallback(async (cursor, tabKey) => {
        setLoading(true)
        const res = await post('micrositeGetVersions', { limit: PAGE_LIMIT, cursor: cursor || 0, tabKey: tabKey || '' })
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
            setNextCursor(res.response?.nextCursor ?? null)
        } else {
            showToast('Could not load version history. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadVersions(currentCursor, tabFilter)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, currentCursor, tabFilter])

    const handleTabFilterChange = (value) => {
        setTabFilter(value)
        setCursorStack([0])
    }

    const handleRollback = async () => {
        if (!rollbackTarget) return
        setRollingBack(true)
        const res = await post('micrositeRollbackVersion', { ID: rollbackTarget.ID })
        setRollingBack(false)

        if (res?.status === 'success') {
            showToast('Rolled back successfully')
            setRollbackTarget(null)
            loadVersions(currentCursor, tabFilter)
        } else {
            showToast('Could not roll back. Please try again.', 'error')
        }
    }

    const formatDate = (d) => {
        if (!d) return '—'
        try {
            return new Date(d.replace(' ', 'T')).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
        } catch {
            return d
        }
    }

    const tabLabel = (key) => TAB_FILTER_OPTIONS.find((o) => o.value === key)?.label || key

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Version history"
                subtitle="See and roll back recent changes across your microsite."
            />

            <div className={styles.toolbar}>
                <SelectInput value={tabFilter} onChange={handleTabFilterChange} options={TAB_FILTER_OPTIONS} />
            </div>

            {loading ? (
                <div className={styles.loadingWrap}>Loading version history…</div>
            ) : rows.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No version history yet</p>
                    <span>Changes you save across the microsite will appear here</span>
                </div>
            ) : (
                <div className={styles.list}>
                    {rows.map((version) => (
                        <div key={version.ID} className={styles.versionRow}>
                            <div className={styles.versionInfo}>
                                <div className={styles.versionTopLine}>
                                    <span className={styles.tabBadge}>{tabLabel(version.tabKey)}</span>
                                    {version.label && (
                                        <span className={styles.labelBadge}>
                                            <Tag size={10} />
                                            {version.label}
                                        </span>
                                    )}
                                </div>
                                <span className={styles.versionMeta}>
                                    {formatDate(version.createdOn)} {version.createdByName ? `· ${version.createdByName}` : ''}
                                </span>
                            </div>
                            <div className={styles.versionActions}>
                                <button
                                    type="button"
                                    className={styles.actionBtn}
                                    onClick={() => showToast('Preview is available from each screen\'s history drawer', 'error')}
                                >
                                    <Eye size={13} />
                                    Preview
                                </button>
                                <button
                                    type="button"
                                    className={styles.actionBtnPrimary}
                                    onClick={() => setRollbackTarget(version)}
                                >
                                    <RotateCcw size={13} />
                                    Roll back
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <MicrositePagination
                hasPrev={cursorStack.length > 1}
                hasNext={!!nextCursor}
                onPrev={() => setCursorStack((s) => s.slice(0, -1))}
                onNext={() => setCursorStack((s) => [...s, nextCursor])}
                loading={loading}
                currentCount={rows.length}
            />

            <MicrositeRollbackConfirmModal
                open={!!rollbackTarget}
                version={rollbackTarget}
                onClose={() => setRollbackTarget(null)}
                onConfirm={handleRollback}
                loading={rollingBack}
            />
        </div>
    )
}

export default MicrositeVersionHistory